import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { getAuthorForSlug } from "@/lib/authors";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useReveal } from "@/hooks/useReveal";
import { getEntry, getRelated } from "@/lib/glossary";
import { glossaryExtras } from "@/lib/glossaryExtras";

export default function GlossarEntry() {
  useReveal();
  const { slug = "" } = useParams();
  const entry = getEntry(slug);

  if (!entry) return <Navigate to="/glossar" replace />;

  const related = getRelated(slug);
  const author = getAuthorForSlug(slug);
  const extras = glossaryExtras[slug] ?? {};
  const faqs = extras.faqs ?? [];
  const blogLinks = extras.blogLinks ?? [];

  const url = `https://finable.de/glossar/${entry.slug}`;

  const termId = `${url}#term`;
  const definedTermSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": termId,
    name: entry.term,
    description: entry.short,
    url,
    inDefinedTermSet: "https://finable.de/glossar#termset",
  };

  const definedTermSetSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": "https://finable.de/glossar#termset",
    name: "FINABLE Finanz-Glossar",
    url: "https://finable.de/glossar",
    hasDefinedTerm: [{ "@id": termId }],
  };

  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${entry.term} – Definition & Erklärung | FINABLE Glossar`}
        description={entry.short}
        canonical={url}
        jsonLd={[definedTermSchema, definedTermSetSchema, faqSchema]}
      />

      <Header />
      <main>
        {/* Header */}
        <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative max-w-3xl">
            <Link
              to="/glossar"
              className="inline-flex items-center gap-2 text-white/70 hover:text-accent text-sm font-medium mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück zum Glossar
            </Link>
            <span className="inline-flex items-center justify-center px-3 h-7 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-[0.12em]">
              {entry.category}
            </span>
            <h1 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-balance leading-[1.1]">
              {entry.term}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/85 leading-relaxed">{entry.short}</p>
          </div>
        </section>

        {/* Body */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container max-w-3xl">
            <article>
              {entry.paragraphs.map((p, i) => (
                <p key={i} className="text-foreground/85 leading-[1.8] text-lg mb-5">
                  {p}
                </p>
              ))}
            </article>

            {/* Internal blog links */}
            {blogLinks.length > 0 && (
              <div className="mt-10 rounded-2xl border border-border bg-card p-6">
                <p className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
                  Weiterlesen
                </p>
                <ul className="space-y-3">
                  {blogLinks.map((b) => (
                    <li key={b.path}>
                      <Link
                        to={b.path}
                        className="group inline-flex items-start gap-2 text-primary hover:text-accent font-semibold transition-colors"
                      >
                        <BookOpen className="w-5 h-5 mt-0.5 shrink-0 text-accent" />
                        <span className="underline-offset-4 group-hover:underline">{b.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FAQ */}
            {faqs.length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-6">
                  Häufige Fragen zu {entry.term}
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left font-display text-base md:text-lg text-primary">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-foreground/80 leading-relaxed text-base">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Author */}
            <div className="mt-14 pt-10 border-t border-border flex items-start gap-5">
              <img
                src={author.image}
                alt={author.name}
                width={72}
                height={72}
                loading="lazy"
                className={`shrink-0 w-[72px] h-[72px] rounded-full object-cover shadow-elegant ${author.imagePosition ?? "object-center"}`}
              />
              <div>
                <p className="font-display text-lg font-bold text-primary">{author.name}</p>
                <p className="text-sm text-accent font-semibold mt-0.5">{author.role} · FINABLE</p>
                <p className="mt-2 text-muted-foreground leading-relaxed">
                  Erklärt Finanzbegriffe verständlich und ohne Fachjargon – damit Du fundierte Entscheidungen für Dein Geld triffst.
                </p>
              </div>
            </div>

            {/* CTA */}
            <aside className="not-prose mt-12 rounded-2xl bg-gradient-hero text-white p-8 md:p-10 shadow-elegant relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-radial opacity-50 pointer-events-none" />
              <div className="relative">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-balance">
                  Du hast Fragen zu {entry.term}?
                </h2>
                <p className="mt-3 text-white/80 leading-relaxed">
                  In unserem kostenlosen Erstgespräch erklären wir Dir alles – unabhängig, digital und bundesweit.
                </p>
                <div className="mt-6">
                  <Button asChild variant="hero" size="lg">
                    <a href="https://calendly.com/finable/kennenlerngespraech" target="_blank" rel="noopener noreferrer">
                      Kostenlos Erstgespräch vereinbaren <ArrowRight className="w-5 h-5" />
                    </a>
                  </Button>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-16 md:py-20 bg-secondary/30">
            <div className="container max-w-4xl">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary text-center mb-10">
                Verwandte Begriffe
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/glossar/${r.slug}`}
                    className="reveal group flex flex-col bg-card border border-border rounded-2xl p-6 hover:border-accent/40 hover:shadow-elegant hover:-translate-y-0.5 transition-all"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                      {r.category}
                    </span>
                    <p className="mt-2 font-display text-lg font-bold text-primary group-hover:text-accent transition-colors">
                      {r.term}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                      {r.short}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                      Mehr erfahren <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
